// weather/model.js — turns the Bureau of Meteorology entities into what
// the weather card and page draw. Pure functions over `hass.states` and
// the hourly forecast, so the rules can be tested without React.
//
// Source: the HACS `bureau_of_meteorology` integration, one config
// entry per place. Each entry names its entities from a prefix:
//   - weather.<p>_hourly      hourly forecast (subscribed, not in states)
//   - sensor.<p>_<field>_<n>  one sensor per field per day, n = 0..6.
//                             BOM's text, rain range, UV and fire danger
//                             exist only here, not on the forecast.
//   - sensor.<obs>_*          the nearest station's observations
//   - sensor.<p>_warnings     count, with BOM's raw warning list attached
//
// Missing or stale data reads as null and the views say so. Nothing here
// falls back to a made-up value: the mock this replaces showed 19° and
// "Partly cloudy" for months as if it were live.

function bomSource(prefix, obsPrefix) {
  return {
    hourly: `weather.${prefix}_hourly`,
    warnings: `sensor.${prefix}_warnings`,
    obs: (field) => `sensor.${obsPrefix}_${field}`,
    day: (field, n) => `sensor.${prefix}_${field}_${n}`,
    DAYS: 7,
  };
}

// The places the Weather page can show. Home comes first and is what
// every other surface (the header's "outside", the home card, the room
// line) always reads. Another place is a second BOM entry in HA plus a
// line here; one whose entities are missing is left off the switch.
// Bundanoon added 2026-10-22 (entry "Bundanoon"; Moss Vale is its
// nearest station).
export const LOCATIONS = [
  { id: 'home', label: 'Home', place: 'Balmain East', src: bomSource('bom', 'bom_observatory_hill') },
  { id: 'bundanoon', label: 'Bundanoon', place: 'Bundanoon', src: bomSource('bundanoon', 'bundanoon_moss_vale') },
];
export const BOM = LOCATIONS[0].src;

// Places HA actually has, home always included.
export function availableLocations(states) {
  return LOCATIONS.filter((l, i) => i === 0 || !!states?.[l.src.day('temp_max', 0)]);
}

// An observation older than this is not "now" any more. The station
// reports every 10-30 minutes.
export const OBS_STALE_MIN = 90;
// BOM reissues the town forecast several times a day; one older than
// this means the integration has stopped updating.
export const FORECAST_STALE_H = 12;
// From this hour the card stops describing a day that is mostly over
// and describes tonight and tomorrow instead.
export const EVENING_HOUR = 17;

const BLANK = new Set(['unknown', 'unavailable', 'none', '']);

function raw(states, id) {
  const e = states?.[id];
  if (!e || e.state == null || BLANK.has(String(e.state).toLowerCase())) return null;
  return e.state;
}
function num(states, id) {
  const v = raw(states, id);
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function time(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}
// Local calendar date, YYYY-MM-DD. BOM's day sensors carry
// `date: 2026-10-22T00:00:00+11:00`, so the prefix is the local date.
export function localDate(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// ── Now ────────────────────────────────────────────────────────────

export function readCurrent(states, now, src = BOM) {
  const tempEntity = states?.[src.obs('temp')];
  const at = time(tempEntity?.attributes?.observation_time);
  const ageMin = at ? (now - at) / 60000 : Infinity;
  const fresh = ageMin <= OBS_STALE_MIN;
  return {
    fresh,
    at,
    station: tempEntity?.attributes?.name?.replace(/^Sydney - /, '') || null,
    temp: fresh ? num(states, src.obs('temp')) : null,
    feels: fresh ? num(states, src.obs('temp_feels_like')) : null,
    humidity: fresh ? num(states, src.obs('humidity')) : null,
    wind: fresh ? num(states, src.obs('wind_speed_kilometre')) : null,
    windDir: fresh ? raw(states, src.obs('wind_direction')) : null,
    gust: fresh ? num(states, src.obs('gust_speed_kilometre')) : null,
    rainSince9am: fresh ? num(states, src.obs('rain_since_9am')) : null,
  };
}

// ── Days ───────────────────────────────────────────────────────────

function readDay(states, n, src) {
  const f = (field) => raw(states, src.day(field, n));
  const nf = (field) => num(states, src.day(field, n));
  const dateAttr = states?.[src.day('short_text', n)]?.attributes?.date
    || states?.[src.day('temp_max', n)]?.attributes?.date;
  const issued = time(states?.[src.day('short_text', n)]?.attributes?.issue_time);
  if (!dateAttr) return null;
  return {
    date: String(dateAttr).slice(0, 10),
    issued,
    // Some places get no short summary from BOM at all (Bundanoon, all
    // seven days, 2026-10-22); the icon descriptor is the Bureau's own
    // one-word call for the day, so it stands in.
    short: f('short_text') || descriptorText(f('icon_descriptor')),
    // HA caps a state at 255 characters and the integration cuts the
    // forecaster's paragraph to fit ("...in the mo..."); the whole text
    // is kept in the sensor's `state` attribute.
    extended: states?.[src.day('extended_text', n)]?.attributes?.state || f('extended_text'),
    icon: f('icon_descriptor'),
    max: nf('temp_max'),
    // BOM's minimum for a day is the low on the morning of that day,
    // i.e. the night before it.
    min: nf('temp_min'),
    rainChance: nf('rain_chance'),
    rainMin: nf('rain_amount_min'),
    rainMax: nf('rain_amount_max'),
    uvCategory: f('uv_category'),
    uvMax: nf('uv_max_index'),
    uvStart: time(f('uv_start_time')),
    uvEnd: time(f('uv_end_time')),
    fire: f('fire_danger'),
    sunrise: time(f('astronomical_sunrise_time')),
    sunset: time(f('astronomical_sunset_time')),
  };
}

const DESCRIPTOR_TEXT = {
  shower: 'Showers.', heavy_shower: 'Heavy showers.', light_shower: 'Light showers.',
  storm: 'Possible storm.', light_rain: 'Light rain.',
};
export function descriptorText(desc) {
  if (!desc) return null;
  if (DESCRIPTOR_TEXT[desc]) return DESCRIPTOR_TEXT[desc];
  const w = String(desc).replace(/_/g, ' ');
  return `${w.charAt(0).toUpperCase()}${w.slice(1)}.`;
}

// Today and the days after it. A day sensor still describing yesterday
// (the minutes after midnight, before BOM's next issue) is dropped.
export function readDays(states, now, src = BOM) {
  const today = localDate(now);
  const days = [];
  for (let n = 0; n < src.DAYS; n++) {
    const d = readDay(states, n, src);
    if (d && d.date >= today) days.push(d);
  }
  const newest = days.reduce((a, d) => (d.issued && (!a || d.issued > a) ? d.issued : a), null);
  const fresh = !!newest && (now - newest) / 3600000 <= FORECAST_STALE_H;
  return fresh ? days : [];
}

// ── Warnings ───────────────────────────────────────────────────────

// Land only, everywhere on the panel (Ben, 2026-10-22). BOM issues
// marine wind and surf warnings for Sydney's waters often enough that
// they would turn the rust "needs you" colour into wallpaper. Types are
// BOM's own, e.g. `marine_wind_warning`, `heatwave_warning`.
const NOT_LAND = /^marine_|hazardous_surf/;

export function landWarnings(states, now, src = BOM) {
  const list = states?.[src.warnings]?.attributes?.warnings;
  if (!Array.isArray(list)) return [];
  return list
    .filter((w) => w && !NOT_LAND.test(w.type || ''))
    .filter((w) => w.phase !== 'cancelled')
    .filter((w) => { const x = time(w.expiry_time); return !x || x > now; })
    .map((w) => ({
      id: w.id,
      title: w.short_title || w.title,
      detail: w.title,
      major: w.warning_group_type === 'major',
      until: time(w.expiry_time),
    }))
    .sort((a, b) => Number(b.major) - Number(a.major));
}

// ── Hours ──────────────────────────────────────────────────────────

function isNightAt(t, days) {
  const d = days.find((x) => x.date === localDate(t));
  if (!d?.sunrise || !d?.sunset) return t.getHours() < 6 || t.getHours() >= 19;
  return t < d.sunrise || t >= d.sunset;
}

export function readHours(forecast, days, now, count = 24) {
  if (!Array.isArray(forecast)) return [];
  const hourStart = new Date(now);
  hourStart.setMinutes(0, 0, 0);
  return forecast
    .map((h) => ({ ...h, t: time(h.datetime) }))
    .filter((h) => h.t && h.t >= hourStart)
    .slice(0, count)
    .map((h) => ({
      t: h.t,
      temp: typeof h.temperature === 'number' ? h.temperature : null,
      chance: typeof h.precipitation_probability === 'number' ? h.precipitation_probability : null,
      mm: typeof h.precipitation === 'number' ? h.precipitation : null,
      icon: iconForCondition(h.condition, isNightAt(h.t, days)),
      wind: h.wind_speed ?? null,
      gust: h.wind_gust_speed ?? null,
    }));
}

// ── The summary the home card leads with ───────────────────────────

// By day: today's forecast. From EVENING_HOUR: tomorrow's, with
// tonight's low, because a forecast for a day that is mostly over
// answers nobody's question.
export function summarise(days, now) {
  const today = days.find((d) => d.date === localDate(now));
  if (!today) return null;
  const evening = now.getHours() >= EVENING_HOUR;
  const tomorrow = days[days.indexOf(today) + 1];
  if (evening && tomorrow) {
    return {
      when: 'Tomorrow',
      short: tomorrow.short,
      icon: iconForDescriptor(tomorrow.icon, false),
      rainChance: tomorrow.rainChance,
      rainMin: tomorrow.rainMin, rainMax: tomorrow.rainMax,
      pair: [{ label: 'Tonight', value: tomorrow.min }, { label: 'Tomorrow', value: tomorrow.max }],
    };
  }
  return {
    when: 'Today',
    short: today.short,
    icon: iconForDescriptor(today.icon, false),
    rainChance: today.rainChance,
    rainMin: today.rainMin, rainMax: today.rainMax,
    pair: [{ label: 'High', value: today.max }, { label: 'Low', value: today.min }],
  };
}

// "8 to 15 mm". BOM gives a range only when it expects rain; below a
// 0-1 mm range the amount is noise and is left off.
export function rainAmount(min, max) {
  if (max == null || max < 2) return null;
  return `${min != null && min !== max ? `${min} to ${max}` : `up to ${max}`} mm`;
}
// "95% chance, 8 to 15 mm", or "30% chance of rain" when there is no
// amount worth giving.
export function rainLine(chance, min, max) {
  if (chance == null) return null;
  const amount = rainAmount(min, max);
  return amount ? `${chance}% chance, ${amount}` : `${chance}% chance of rain`;
}

// ── Icons ──────────────────────────────────────────────────────────
// Keys are drawn in weather/icons.jsx.

const DESCRIPTOR = {
  sunny: 'sun', clear: 'sun', mostly_sunny: 'partSun', partly_cloudy: 'partSun',
  cloudy: 'cloud', hazy: 'haze', haze: 'haze', dusty: 'haze', dust: 'haze',
  light_rain: 'drizzle', light_shower: 'drizzle', shower: 'rain', rain: 'rain',
  heavy_shower: 'heavy', storm: 'storm', cyclone: 'storm', tropical_cyclone: 'storm',
  wind: 'wind', windy: 'wind', fog: 'fog', frost: 'snow', snow: 'snow',
};
const CONDITION = {
  sunny: 'sun', 'clear-night': 'moon', partlycloudy: 'partSun', cloudy: 'cloud',
  rainy: 'rain', pouring: 'heavy', lightning: 'storm', 'lightning-rainy': 'storm',
  fog: 'fog', windy: 'wind', 'windy-variant': 'wind', snowy: 'snow',
  'snowy-rainy': 'snow', hail: 'snow', exceptional: 'cloud',
};
const AT_NIGHT = { sun: 'moon', partSun: 'partMoon' };

export function iconForDescriptor(desc, night) {
  const k = DESCRIPTOR[desc] || 'cloud';
  return night ? (AT_NIGHT[k] || k) : k;
}
export function iconForCondition(cond, night) {
  const k = CONDITION[cond] || 'cloud';
  return night ? (AT_NIGHT[k] || k) : k;
}

// ── Formatting ─────────────────────────────────────────────────────

export const fmtHour = (d) => String(d.getHours()).padStart(2, '0');
export const fmtClock = (d) => `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
export function fmtDay(dateStr, now) {
  if (dateStr === localDate(now)) return 'Today';
  const d = new Date(`${dateStr}T12:00:00`);
  return d.toLocaleDateString('en-AU', { weekday: 'short' });
}
// "Showers." → "Showers", for headings; BOM writes sentences.
export const asHeading = (s) => (s ? s.replace(/\.$/, '') : null);
// "Showers." → "showers", for the room-header line.
export const asPhrase = (s) => (s ? s.replace(/\.$/, '').toLowerCase() : null);
